<template>
  <SettingsPageLayout>
    <QuickstartCard v-if="$route.query.quickstart">
      <template #header>👋 Invite your teammates</template>
      <template #body>
        Invite your supporting teammates to Humaniz. These users are typically Recruiters, Sales Managers, Operations,
        etc. who help you manage your team.
      </template>
    </QuickstartCard>
    <DetailForm
      title="Invite New User"
      description="Invites a new user and adds them to your team as a Contributor."
      action="Send"
      hidecancel="true"
      data-intro="Let's get your first user added to your team. If you have a partner, recruiter, or sales manager, add them here."
      @handle-action="createUser()"
    >
      <div class="grid grid-cols-3 gap-x-4">
        <TextInput
          class="col-span-2"
          label="Email"
          placeholder="test@example.com"
          :model-value="user.email"
          @update:model-value="value => (user.email = value)"
        />
        <SelectMenu
          class="col-span-1"
          label="Role"
          initial-value="Admin"
          :options="['Admin', 'Contributor']"
          @selected-option="user.role = $event"
        />
      </div>
    </DetailForm>
    <div class="mt-6">
      <div v-if="users.length > 0" class="flex flex-col">
        <DataTable :columns="dtColumns" :rows="rows" :sort="sort">
          <template #td-email="slotProps">
            {{ slotProps.row.email }}
          </template>
          <template #td-assignments="slotProps">
            <div class="flex flex-wrap gap-1">
              <Badge v-if="team.phone_screen_user_id == slotProps.row.id" variant="blue"
                >Assigned to Phone Screen</Badge
              >
              <Badge v-if="team.interview_user_id == slotProps.row.id" variant="green">Assigned to Interview</Badge>
              <Badge v-if="team.esigner_user_id == slotProps.row.id" variant="indigo-dark"
                >Assigned to E-signatures</Badge
              >
            </div>
          </template>
          <template #td-status="slotProps">
            <Badge v-if="slotProps.row.invited" variant="yellow">Invited</Badge>
            <Badge v-else-if="slotProps.row.status == 'active'" variant="green">Active</Badge>
            <Badge v-else variant="red">Inactive</Badge>
          </template>
          <template #td-action="slotProps">
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <button
                  class="inline-flex justify-center rounded-md border border-gray-300 px-2 py-1 bg-white text-sm font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-indigo-300"
                >
                  <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuItem v-if="slotProps.row.invited" @select="resendInvite(slotProps.row.id)">
                    <vue-feather type="mail" class="mr-2 h-4 w-4" />
                    Resend Invite
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    v-if="slotProps.row.role == 'Contributor'"
                    @select="updateUser('role', 'Admin', slotProps.row.id)"
                  >
                    <vue-feather type="repeat" class="mr-2 h-4 w-4" />
                    Change to Admin
                  </DropdownMenuItem>
                  <DropdownMenuItem v-else @select="updateUser('role', 'Contributor', slotProps.row.id)">
                    <vue-feather type="repeat" class="mr-2 h-4 w-4" />
                    Change to Contributor
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator v-if="slotProps.row.role != 'Agent'" />
                <DropdownMenuGroup v-if="slotProps.row.role != 'Agent'">
                  <DropdownMenuItem @select="assignToPhoneScreen(slotProps.row.id)">
                    <vue-feather type="phone" class="mr-2 h-4 w-4" />
                    Assign to Phone Screen
                  </DropdownMenuItem>
                  <DropdownMenuItem @select="assignToInterview(slotProps.row.id)">
                    <vue-feather type="briefcase" class="mr-2 h-4 w-4" />
                    Assign to Interview
                  </DropdownMenuItem>
                  <DropdownMenuItem @select="assignToEsigner(slotProps.row.id)">
                    <vue-feather type="feather" class="mr-2 h-4 w-4" />
                    Assign to E-signatures
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    v-if="slotProps.row.status == 'active'"
                    @select="updateUser('status', 'inactive', slotProps.row.id)"
                  >
                    <vue-feather type="lock" class="mr-2 h-4 w-4" />
                    Deactivate user
                  </DropdownMenuItem>
                  <DropdownMenuItem v-else @select="updateUser('status', 'active', slotProps.row.id)">
                    <vue-feather type="unlock" class="mr-2 h-4 w-4" />
                    Activate User
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </template>
        </DataTable>
      </div>
      <div v-else>
        <LoadingList />
      </div>
    </div>
  </SettingsPageLayout>
</template>

<script>
import SettingsPageLayout from '../../layouts/SettingsPageLayout/index.vue';
import DetailForm from '../../shared/DetailForm.vue';
import LoadingList from '../../shared/LoadingList.vue';
import DataTable from '../../shared/DataTable/DeprecatedDataTable.vue';
import { formatStandardDateTime } from '../../../utilities/format.ts';
import { createUser } from '../../../api/users.ts';
import QuickstartCard from '../../shared/QuickstartCard.vue';
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { toast } from 'vue-sonner';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default {
  components: {
    Badge,
    SettingsPageLayout,
    DetailForm,
    LoadingList,
    DataTable,
    QuickstartCard,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  },
  setup() {
    const currentUserStore = useCurrentUserStore();
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { currentUserStore, loadingSpinnerStore };
  },
  data() {
    return {
      user: {
        email: '',
        role: '',
      },
      team: null,
      users: [],
    };
  },
  computed: {
    dtColumns() {
      return [
        {
          label: 'Name',
          key: 'name',
          sortable: false,
        },
        {
          label: 'Email',
          key: 'email',
          sortable: false,
        },
        {
          label: 'Status',
          key: 'status',
          sortable: false,
        },
        {
          label: 'Role',
          key: 'role',
          sortable: false,
        },
        {
          label: 'Assignments',
          key: 'assignments',
          sortable: false,
        },
        {
          label: '# of Logins',
          key: 'sign_in_count',
          sortable: false,
        },
        {
          label: 'Created At',
          key: 'created_at',
          formatter: formatStandardDateTime,
          sortable: false,
        },
        {
          label: 'Action',
          key: 'action',
          sortable: false,
        },
      ];
    },
    rows() {
      return this.users;
    },
    sort() {
      return {
        key: 'created_at',
        order: 'desc',
      };
    },
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    getUsers() {
      const self = this;
      fetch('/api/team')
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.users = data.team_members;
          self.team = data.record;
        });
    },
    async createUser() {
      this.loadingSpinnerStore.showLocalLoadingSpinner();
      const response = await createUser({
        email: this.user.email,
        role: this.user.role,
      });
      this.loadingSpinnerStore.hideLocalLoadingSpinner();

      if (response.status === 'error') {
        const description = response.message;
        toast.error('Failed to Create User', { description });
        return;
      }

      // This is a temporary solution the Services::Users::Create service always being success (even during failures).
      if (!response.record.id) {
        const description = `${this.user.email} may already exist`;
        toast.error('Failed to Create User', { description });
        return;
      }

      this.getUsers();
      this.user.email = '';
      toast.success('Team Member Created', {
        action: { label: 'Dismiss' },
        description: 'Your New Team Member was successfully created.  Use the button below to dismiss this alert.',
      });
    },
    async updateUser(column, e, userId) {
      this.loadingSpinnerStore.showLocalLoadingSpinner();
      try {
        let body = {};
        body[column] = e;
        await this.currentUserStore.updateUser({
          user: { id: userId },
          updatedUserAttributes: body,
        });
        this.getUsers();
        toast.success('User updated', {
          action: { label: 'Dismiss' },
          description: 'Your User was successfully updated.  Use the button below to dismiss this alert.',
        });
      } finally {
        this.loadingSpinnerStore.hideLocalLoadingSpinner();
      }
    },
    async resendInvite(userId) {
      this.loadingSpinnerStore.showLocalLoadingSpinner();
      await this.currentUserStore.updateUser({
        user: { id: userId },
        updatedUserAttributes: { resend_invite: true },
      });
      this.getUsers();
      this.loadingSpinnerStore.hideLocalLoadingSpinner();
      toast.success('Invite resent!', {
        action: { label: 'Dismiss' },
        description: 'Your user invite was successfully resent.  Use the button below to dismiss this alert.',
      });
    },
    assignToPhoneScreen(userId) {
      const self = this;
      this.loadingSpinnerStore.showLocalLoadingSpinner();
      fetch('/api/team', {
        method: 'PUT',
        body: JSON.stringify({
          phone_screen_user_id: userId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.getUsers();
          if (data.status != 'error') {
            toast.success('Phone screen user updated', {
              action: { label: 'Dismiss' },
              description: 'Your team was successfully updated.  Use the button below to dismiss this alert.',
            });
          } else {
            toast.error('Phone screen update error', {
              action: { label: 'Dismiss' },
              description: data.message,
            });
          }
        })
        .finally(() => this.loadingSpinnerStore.hideLocalLoadingSpinner());
    },
    assignToInterview(userId) {
      const self = this;
      this.loadingSpinnerStore.showLocalLoadingSpinner();
      fetch('/api/team', {
        method: 'PUT',
        body: JSON.stringify({
          interview_user_id: userId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.getUsers();
          if (data.status != 'error') {
            toast.success('Interview user updated', {
              action: { label: 'Dismiss' },
              description: 'Your team was successfully updated.  Use the button below to dismiss this alert.',
            });
          } else {
            toast.error('Interview update error', {
              action: { label: 'Dismiss' },
              description: data.message,
            });
          }
        })
        .finally(() => this.loadingSpinnerStore.hideLocalLoadingSpinner());
    },
    assignToEsigner(userId) {
      const self = this;
      this.loadingSpinnerStore.showLocalLoadingSpinner();
      fetch('/api/team', {
        method: 'PUT',
        body: JSON.stringify({
          esigner_user_id: userId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.getUsers();
          if (data.status != 'error') {
            toast.success('E-signature user updated', {
              action: { label: 'Dismiss' },
              description: 'Your team was successfully updated.  Use the button below to dismiss this alert.',
            });
          } else {
            toast.error('E-signature user update error', {
              action: { label: 'Dismiss' },
              description: data.message,
            });
          }
        })
        .finally(() => this.loadingSpinnerStore.hideLocalLoadingSpinner());
    },
  },
};
</script>
